import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChange,
  Renderer2,
} from "@angular/core";

import { Calendar } from "./calendar";
import {
  animate,
  keyframes,
  transition,
  trigger,
  style,
} from "@angular/animations";

@Component({
  selector: "datepicker",
  animations: [
    trigger("calendarAnimation", [
      transition("* => left", [
        animate(
          180,
          keyframes([
            style({ transform: "translateX(105%)", offset: 0.5 }),
            style({ transform: "translateX(-130%)", offset: 0.51 }),
            style({ transform: "translateX(0)", offset: 1 }),
          ]),
        ),
      ]),
      transition("* => right", [
        animate(
          180,
          keyframes([
            style({ transform: "translateX(-105%)", offset: 0.5 }),
            style({ transform: "translateX(130%)", offset: 0.51 }),
            style({ transform: "translateX(0)", offset: 1 }),
          ]),
        ),
      ]),
    ]),
  ],
  styleUrls: ["datepicker.component.scss"],
  templateUrl: "./datepicker.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatepickerComponent implements OnInit, OnChanges {
  private dateVal: Date;

  hover: any;
  showCalendar: boolean;

  // two way bindings
  @Output() dateChange = new EventEmitter<Date>();

  @Input()
  get date(): Date {
    return this.dateVal;
  }

  set date(val: Date | number) {
    if (val instanceof Date) {
      this.dateVal = val;
      if (this.initialized) {
        this.dateChange.emit(val);
      }
    }
  }

  // api bindings
  @Input() disabled: boolean;
  @Input() accentColor: string;
  @Input() altInputStyle: boolean;
  @Input() dateFormat: string;
  @Input() fontFamily: string;
  @Input() rangeStart: Date;
  @Input() rangeEnd: Date;
  // data
  @Input() placeholder = "Select a date";
  @Input() inputText: string;
  // view logic
  // events
  @Output() onSelect = new EventEmitter<Date>();
  @Output() onEscape = new EventEmitter();
  // time
  @Input() calendarDays: (Date | number)[];
  @Input() currentMonth: string;
  @Input() dayNames: Array<String>;
  @Input() hoveredDay: Date | number | null;
  calendar: Calendar;
  currentMonthNumber: number;
  currentYear: number;
  months: Array<string>;
  // animation
  animate: string;
  // colors
  colors: { [id: string]: string };
  // listeners
  clickListener: Function;
  initialized = false;

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
  ) {
    this.dateFormat = "YYYY-MM-DD";
    // view logic
    this.showCalendar = true;
    // colors
    this.colors = {
      black: "#333333",
      blue: "#1285bf",
      lightGrey: "#f1f1f1",
      white: "#ffffff",
      hover: "#eef2f6",
    };
    this.accentColor = this.colors["hover"];
    this.altInputStyle = false;
    // time
    this.calendar = new Calendar();
    this.dayNames = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    this.months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      " December",
    ];
    // listeners
    this.clickListener = renderer.listen(
      "document",
      "keyup.enter",
      (event: KeyboardEvent) => this.doEmit(),
    );
    renderer.listen("document", "keyup.escape", (event: KeyboardEvent) =>
      this.abort(),
    );
  }

  ngOnInit() {
    if (!this.date) {
      this.date = new Date();
    }
    this.setDate();
    this.initialized = true;
  }

  ngOnChanges(changes: { [propertyName: string]: SimpleChange }) {
    if (changes["date"] || changes["dateFormat"]) {
      this.setDate();
    }
  }

  doEmit() {
    this.onSelect.emit(this.dateVal);
  }

  ngOnDestroy() {
    // this.clickListener();
  }

  // State Management
  // ------------------------------------------------------------------------------------
  closeCalendar(): void {
    this.setDate();
  }

  private setCurrentValues(date: Date) {
    this.currentMonthNumber = date.getMonth();
    this.currentMonth = this.months[this.currentMonthNumber];
    this.currentYear = date.getFullYear();
    const calendarArray = this.calendar.monthDays(
      this.currentYear,
      this.currentMonthNumber,
    );
    this.calendarDays = [].concat.apply([], calendarArray);
  }

  setDate(): void {
    if (this.date) {
      this.setInputText(this.date);
      this.setCurrentValues(this.date);
    } else {
      this.inputText = "";
      this.setCurrentValues(new Date());
    }
  }

  setCurrentMonth(monthNumber: number) {
    this.currentMonth = this.months[monthNumber];
    const calendarArray = this.calendar.monthDays(
      this.currentYear,
      this.currentMonthNumber,
    );
    this.calendarDays = [].concat.apply([], calendarArray);
  }

  setHoveredDay(day: Date | number): void {
    this.hoveredDay = day;
  }

  removeHoveredDay(_day: Date | number): void {
    this.hoveredDay = null;
  }

  setInputText(date: Date): void {
    let month: string = (date.getMonth() + 1).toString();
    if (month.length < 2) {
      month = `0${month}`;
    }
    let day: string = date.getDate().toString();
    if (day.length < 2) {
      day = `0${day}`;
    }

    let inputText: string;
    switch (this.dateFormat.toUpperCase()) {
      case "YYYY-MM-DD":
        inputText = `${date.getFullYear()}/${month}/${day}`;
        break;
      case "MM-DD-YYYY":
        inputText = `${month}/${day}/${date.getFullYear()}`;
        break;
      case "DD-MM-YYYY":
        inputText = `${day}/${month}/${date.getFullYear()}`;
        break;
      default:
        inputText = `${date.getFullYear()}/${month}/${day}`;
        break;
    }

    this.inputText = inputText;
  }

  // Click Handlers
  // ------------------------------------------------------------------------------------
  onArrowLeftClick(): void {
    const currentMonth: number = this.currentMonthNumber;
    let newYear: number = this.currentYear;
    let newMonth: number;

    if (currentMonth === 0) {
      newYear = this.currentYear - 1;
      newMonth = 11;
    } else {
      newMonth = currentMonth - 1;
    }

    const newDate = new Date(newYear, newMonth);
    if (!this.rangeStart || newDate.getTime() >= this.rangeStart.getTime()) {
      this.currentYear = newYear;
      this.currentMonthNumber = newMonth;
      this.setCurrentMonth(newMonth);
      this.triggerAnimation("left");
    }
  }

  onArrowRightClick(): void {
    const currentMonth: number = this.currentMonthNumber;
    let newYear: number = this.currentYear;
    let newMonth: number;

    if (currentMonth === 11) {
      newYear = this.currentYear + 1;
      newMonth = 0;
    } else {
      newMonth = currentMonth + 1;
    }

    const newDate = new Date(newYear, newMonth);
    if (!this.rangeEnd || newDate.getTime() <= this.rangeEnd.getTime()) {
      this.currentYear = newYear;
      this.currentMonthNumber = newMonth;
      this.setCurrentMonth(newMonth);
      this.triggerAnimation("right");
    }
  }

  onInputClick(): void {
    this.showCalendar = !this.showCalendar;
  }

  onSelectDay(event): void {
    // this.date = new Date(this.calendarDays[Number(event.target.innerText) + 3]);
    this.date = event;
  }

  // Listeners
  // ------------------------------------------------------------------------------------
  handleGlobalClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.closeCalendar();
    }
  }

  // Helpers
  // ------------------------------------------------------------------------------------
  getDayBackgroundColor(day: Date | number): string {
    if (day === 0 || typeof day === 'number') return this.colors["white"];
    let color = this.colors["white"];
    if (this.isChosenDay(day)) {
      color = this.accentColor;
    }
    return color;
  }

  getDayFontColor(day: Date | number): string {
    if (day === 0 || typeof day === 'number') return this.colors["black"];
    let color = this.colors["black"];
    if (this.isChosenDay(day)) {
      color = this.colors["white"];
    }
    return color;
  }

  isChosenDay(day: Date | number): boolean {
    if (day === 0 || typeof day === 'number' || !day) return false;
    return this.date ? day.toDateString() === this.date.toDateString() : false;
  }

  isHoveredDay(day: Date | number): boolean {
    if (day === 0 || typeof day === 'number') return false;
    return this.hoveredDay
      ? this.hoveredDay === day && !this.isChosenDay(day)
      : false;
  }

  triggerAnimation(direction: string): void {
    this.animate = direction;
    setTimeout(() => (this.animate = "reset"), 185);
  }

  setHover(day) {
    this.hover = day;
  }

  isSelected(day: Date | number): boolean {
    if (day === 0 || typeof day === 'number') return false;
    return day === this.dateVal;
  }

  abort() {
    this.onEscape.emit();
  }

  isWeekendDay(index) {
    while (index > 7) {
      index -= 7;
    }
    return index % 6 === 0 || index % 7 === 0;
  }

  getDayDisplay(day: Date | number): number | '' {
    return day instanceof Date ? day.getDate() : '';
  }
}
